import { Component, signal, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ProjectService } from '../../../core/services/project.service';
import { CustomStatus, Project, StatusType, MetaStatus } from '../../../core/models/models';
import { ProjectCreateDialogComponent } from '../create/project-create-dialog.component';
import { CustomStatusService } from '../../../core/services/custom-status.service';
import { AuthService } from '../../../core/services/auth.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog.component';

@Component({
  selector: 'app-project-list',
  standalone: true,
  imports: [CommonModule, ProjectCreateDialogComponent, FormsModule, RouterLink, MatPaginatorModule, MatSortModule, MatIconModule, MatSnackBarModule, ConfirmDialogComponent],
  templateUrl: './project-list.component.html',
  styleUrl: './project-list.component.scss'
})
export class ProjectListComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  private projectService = inject(ProjectService);
  private customStatusService = inject(CustomStatusService);
  public authService = inject(AuthService);

  projects = signal<Project[]>([]);
  statusOptions = signal<CustomStatus[]>([]);
  isDialogOpen = signal(false);
  showDeleteDialog = signal(false);
  projectToDelete = signal<Project | null>(null);
  isAdmin = this.authService.isAdmin;
  showArchived = signal(false);

  // Pagination and search
  searchQuery = '';
  selectedStatusId: number | null = null;
  page = signal(0);
  size = signal(10);
  sortField = 'name';
  sortDirection = 'asc';
  totalElements = signal(0);
  totalPages = signal(0);
  Math = Math;

  private searchSubject = new Subject<string>();

  constructor(private snackBar: MatSnackBar) { }

  canDeleteProject(project: Project): boolean {
    return !!project && project.metaStatus === MetaStatus.DRAFT;
  }

  deleteProject(project: Project): void {
    this.projectToDelete.set(project);
    this.showDeleteDialog.set(true);
  }

  confirmDelete(): void {
    const project = this.projectToDelete();
    if (project && project.id) {
      this.projectService.deleteProject(project.id).subscribe({
        next: () => {
          this.snackBar.open('Projet supprimé avec succès', 'Fermer', { duration: 3000 });
          this.showDeleteDialog.set(false);
          this.projectToDelete.set(null);
          this.loadProjects();
        },
        error: (err) => {
          console.error('Error deleting project:', err);
          this.snackBar.open('Erreur lors de la suppression du projet', 'Fermer', { duration: 3000 });
        }
      });
    }
  }

  cancelDelete(): void {
    this.showDeleteDialog.set(false);
    this.projectToDelete.set(null);
  }

  ngOnInit(): void {
    this.loadStatusOptions();
    this.loadProjects();

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.page.set(0);
      this.loadProjects();
    });
  }

  loadProjects(): void {
    const sortParam = `${this.sortField},${this.sortDirection}`;
    this.projectService.getProjects(this.searchQuery, this.page(), this.size(), sortParam, this.selectedStatusId, this.showArchived()).subscribe(response => {
      this.projects.set(response.content);
      this.totalElements.set(response.totalElements);
      this.totalPages.set(response.totalPages);
    });
  }

  loadStatusOptions(): void {
    this.customStatusService.getAll(StatusType.PROJECT).subscribe({
      next: (statuses) => {
        this.statusOptions.set(statuses);
      }
    });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex);
    this.size.set(event.pageSize);
    this.loadProjects();
  }

  onSortChange(sortState: Sort): void {
    this.sortField = sortState.active;
    this.sortDirection = sortState.direction || 'asc';
    this.page.set(0);
    this.loadProjects();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getStatusLabel(status: string): string {
    return status;
  }

  onStatusFilterChange(value: string): void {
    this.selectedStatusId = value ? Number(value) : null;
    this.page.set(0);
    this.loadProjects();
  }

  onToggleArchived(): void {
    this.showArchived.set(!this.showArchived());
    this.page.set(0);
    this.loadProjects();
  }

  openCreateDialog(): void {
    this.isDialogOpen.set(true);
  }

  closeCreateDialog(): void {
    this.isDialogOpen.set(false);
  }

  onProjectCreated(): void {
    this.loadProjects();
    this.closeCreateDialog();
  }
}
